import { useCallback, useEffect, useState } from 'react'
import { useHighlightsApi } from './useHighlightsApi'

// Resolution precedence for "which sport profile is this session viewing":
//   1. URL query param ?profile=<key>  (deep-linkable / shareable)
//   2. localStorage 'tbtv:highlights:profile' (per-editor preference)
//   3. is_default = 1 from /profiles (site-wide default; currently hockey)
//   4. Hardcoded 'hockey' (final safety net)
//
// Also exposes the enabled profile list for the header dropdown, and a
// setProfile(key) that updates both the URL param and localStorage.
//
// Cached per module load — the profile list is small (< 5 rows expected)
// and rarely changes; we refresh on setProfile calls only.

const LS_KEY = 'tbtv:highlights:profile'
const URL_PARAM = 'profile'

function readUrl() {
  try {
    const p = new URL(window.location.href).searchParams.get(URL_PARAM)
    return p || null
  } catch { return null }
}

function readLs() {
  try { return localStorage.getItem(LS_KEY) || null }
  catch { return null }
}

function writeUrl(key) {
  try {
    const u = new URL(window.location.href)
    if (key) u.searchParams.set(URL_PARAM, key); else u.searchParams.delete(URL_PARAM)
    window.history.replaceState({}, '', u.toString())
  } catch {}
}

function writeLs(key) {
  try { localStorage.setItem(LS_KEY, key) }
  catch {}
}

export function useCurrentProfile() {
  const api = useHighlightsApi()
  const [profiles, setProfiles] = useState([])
  const [profileKey, setProfileKeyState] = useState(() =>
    readUrl() || readLs() || 'hockey',
  )
  const [loading, setLoading] = useState(true)

  // Load the enabled profile list once + reconcile the current key against it.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api.listProfiles({ enabledOnly: true })
      .then(res => {
        if (cancelled) return
        const list = Array.isArray(res?.profiles) ? res.profiles : []
        setProfiles(list)
        // If nothing loaded, keep whatever we have; hockey fallback is fine.
        if (!list.length) { setLoading(false); return }
        // If the current key isn't in the enabled list, snap to the default.
        const current = readUrl() || readLs() || profileKey
        const found = list.find(p => p.profile_key === current)
        if (!found) {
          const dflt = list.find(p => p.is_default) || list[0]
          setProfileKeyState(dflt.profile_key)
          writeLs(dflt.profile_key)
        } else if (current !== profileKey) {
          setProfileKeyState(current)
        }
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        // Network / auth failure — keep hockey fallback; UI stays functional.
        setLoading(false)
      })
    return () => { cancelled = true }
    // Intentionally exclude profileKey from deps — this effect only runs
    // once on mount to load the list; setProfile handles subsequent changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api])

  const setProfile = useCallback((key) => {
    if (!key || key === profileKey) return
    writeUrl(key)
    writeLs(key)
    setProfileKeyState(key)
  }, [profileKey])

  return { profileKey, setProfile, profiles, loading }
}
